import { Box, ExpandableSection, SpaceBetween, Spinner } from '@amzn/awsui-components-core';
import {
  ContentQualityQuestionData,
  QuestionResponse,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState } from 'react';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import {
  CategoryOrder,
  createSortedCategories,
  populateEvaluationMaps,
} from '../utils/ContentQualityUtils';
import QuestionnaireCategoryBox from './QuestionnaireCategoryBox';
import Barometer from './Barometer';

interface Props {
  contentId: string;
  readonly?: boolean;
}

let mplServiceClient: IMplServiceClient;

const ContentQualityEvalucationCard: React.FunctionComponent<Props> = (props: Props) => {
  const { contentId, readonly } = props;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [categories, setCategories] = useState<CategoryOrder[]>([]);
  const [sequenceToQuestionMap, setSequenceToQuestionMap] = useState<
    Map<number, ContentQualityQuestionData>
  >(new Map<number, ContentQualityQuestionData>());
  const [categoryToSequencesMap, setCategoryToSequencesMap] = useState<Map<string, number[]>>(
    new Map<string, number[]>(),
  );
  const [sequenceToResponseMap, setSequenceToResponseMap] = useState<Map<number, QuestionResponse>>(
    new Map<number, QuestionResponse>(),
  );
  const [qualityScore, setQualityScore] = useState<number>(-1);

  useEffect(() => {
    async function loadEvaluation() {
      setIsLoading(true);
      mplServiceClient = MplServiceClientFactory.create();
      const questionSequenceToQuestionMap = new Map<number, ContentQualityQuestionData>();
      const questionCategoryToSequencesMap = new Map<string, number[]>();
      const questionSequenceToResponseMap = new Map<number, QuestionResponse>();
      const questionCategories: CategoryOrder[] = [];
      let score = -1;
      try {
        const evaluationResponse =
          await mplServiceClient.getLastContentQualityEvaluation(contentId);
        const { responses } = evaluationResponse;
        score = evaluationResponse.score;
        // populate the maps
        populateEvaluationMaps(
          responses,
          questionSequenceToQuestionMap,
          questionCategoryToSequencesMap,
          questionSequenceToResponseMap,
        );
        createSortedCategories(questionCategoryToSequencesMap, questionCategories);

        setCategories(questionCategories);
        setSequenceToQuestionMap(questionSequenceToQuestionMap);
        setCategoryToSequencesMap(questionCategoryToSequencesMap);
        setSequenceToResponseMap(questionSequenceToResponseMap);
        setQualityScore(score);
      } finally {
        setIsLoading(false);
      }
    }

    loadEvaluation();
  }, [contentId]);

  const evaluationsInCategories = categories.map((category, index) => {
    const sequences = categoryToSequencesMap.get(category.category) as number[];
    return (
      <div key={category.category}>
        <QuestionnaireCategoryBox
          key={category.category}
          category={category.category}
          sequences={sequences}
          sequenceToQuestionMap={sequenceToQuestionMap}
          sequenceToResponseMap={sequenceToResponseMap}
          readonly
        />
      </div>
    );
  });

  const expandableSectionVariant = !!readonly ? 'default' : 'container';

  const evaluationResult = (
    <ExpandableSection
      headerText="Last bar raiser evaluation"
      variant={expandableSectionVariant}
      defaultExpanded
    >
      <Box margin={{ left: 'xxs', right: 'xxs' }}>
        <SpaceBetween direction="vertical" size="xxxs">
          <div id="evaluation-barometer">
            <Barometer score={qualityScore} />
          </div>
          <Box textAlign="center">
            <Box variant="p">Technical quality score</Box>
            <Box variant="p">
              <Box variant="strong">{qualityScore < 0 ? '--' : qualityScore}</Box> / 100
            </Box>
          </Box>
          {evaluationsInCategories}
        </SpaceBetween>
      </Box>
    </ExpandableSection>
  );

  const cardContainer = readonly ? (
    <article>{evaluationResult}</article>
  ) : (
    <Box padding="s">{evaluationResult}</Box>
  );

  const evaluationCard = sequenceToResponseMap.size > 0 ? cardContainer : null;

  return isLoading ? <Spinner size={'normal'} /> : evaluationCard;
};

export default ContentQualityEvalucationCard;
